import { AnimatePresence, m } from "framer-motion";
import { useTranslation } from "gatsby-plugin-react-i18next";
import { createRef, useEffect, useRef } from "react";
import { RemoveScroll } from "react-remove-scroll";

import { Box, NavMobileCategory, ThemeWrapper } from "@theme/components";

import { NavMobileDropdown } from "@theme/components/Navigation/NavMobileDropdown";
import { NavMobileMainScreen } from "@theme/components/Navigation/NavMobileMainScreen";
import { NavMobileSecondaryScreen } from "@theme/components/Navigation/NavMobileSecondaryScreen";

import { useClickOutside, useDrawer } from "@theme/utils/hooks";

import { useNavHeight } from "@theme/utils";

import type { NavigationDataProps } from "@theme/types";
import type { FC } from "react";

interface NavMobileDrawerProps {
  navData: NavigationDataProps;
}

const NavLinkTextStyles = {
  color: "grey700",
  lineHeight: "1.75rem",
  letterSpacing: "-0.009rem",
};

// Uses global menu state atom (activeMenuAtom) to control mobile drawer visibility.
export const NavMobileDrawer: FC<NavMobileDrawerProps> = ({ navData }) => {
  const [
    { isOpen, currentView },
    { closeDrawer, showCategory, showMain, showDropdown, openDrawer },
  ] = useDrawer();
  const mobileDrawerRef = useRef<HTMLDivElement>(null);
  const { navHeights } = useNavHeight();

  const categoryRefs = useRef(
    Array(navData.siteNavData?.mobileCategories?.length ?? 0)
      .fill(null)
      .map(() => createRef<HTMLElement>()),
  );

  // ===== Effects =====
  useEffect(() => {
    if (mobileDrawerRef.current) {
      mobileDrawerRef.current.scrollTop = 0;
    }
  }, [mobileDrawerRef]);

  // Focus on the first navigation element when we open the mobile drawer
  useEffect(() => {
    if (isOpen) {
      categoryRefs.current[0].current?.focus();
    }
  }, [isOpen]);

  const showAccordion = (index: number) => {
    const category = navData.siteNavData?.mobileCategories?.[index];
    if (category) {
      showCategory(category);
    }
  };

  const { t } = useTranslation("common-phrases");

  useClickOutside(mobileDrawerRef, () => {
    if (isOpen) {
      closeDrawer();
    }
  });

  return (
    <AnimatePresence>
      {isOpen && (
        <ThemeWrapper theme="LIGHT">
          <Box sx={{ display: ["block", null, null, "none"] }} aria-hidden={!isOpen}>
            <RemoveScroll enabled={isOpen}>
              <m.menu
                data-dropdown="mobile-menu"
                aria-label="Mobile Menu Dropdown"
                variants={{
                  hide: { y: "-110%" },
                  show: { y: 0 },
                }}
                initial="hide"
                animate={isOpen ? "show" : "hide"}
                transition={{ ease: [1, 0, 0, 1], duration: 0.3 }}
                ref={mobileDrawerRef}
                sx={{
                  outline: "none",
                  overflow: "hidden",
                  width: "full",
                  position: "absolute",
                  top: "100%",
                  left: 0,
                  p: 0,
                  m: 0,
                  bg: "beige50",
                  overflowY: "scroll",
                  overflowX: "hidden",
                  height: navHeights.map((height) => `calc(100vh - ${height}px)`),
                  zIndex: -1,
                }}
                onKeyDown={(e) => {
                  if (e.key === "Escape") {
                    closeDrawer();
                  }
                }}
              >
                <NavMobileMainScreen
                  navData={navData}
                  categoryRefs={categoryRefs}
                  NavLinkTextStyles={NavLinkTextStyles}
                  showAccordion={showAccordion}
                  drawerState={{
                    isOpen,
                    currentView,
                    closeDrawer,
                    showCategory,
                    showMain,
                    showDropdown,
                    openDrawer,
                  }}
                />

                {currentView.type === "dropdown" && navData.dropdownLinks && (
                  <NavMobileSecondaryScreen
                    title={
                      currentView.index === navData.dropdownLinks.length
                        ? `${t("commonLanguage", {
                            defaultValue: "Language",
                          })}: ${navData.currentLanguage.toUpperCase()}`
                        : navData.dropdownLinks[currentView.index].title
                    }
                    backCTAText={navData.backCTAText}
                    closeAccordion={showMain}
                  >
                    <NavMobileDropdown
                      navData={navData}
                      NavLinkTextStyles={NavLinkTextStyles}
                      selectedDropdownIndex={currentView.index}
                      closeDrawer={closeDrawer}
                    />
                  </NavMobileSecondaryScreen>
                )}

                {currentView.type === "account" && (
                  <NavMobileSecondaryScreen
                    title={
                      navData.accountAuthenticatedLink?.mobileLabel ??
                      navData.accountAuthenticatedLink?.label ??
                      ""
                    }
                    backCTAText={navData.backCTAText}
                    closeAccordion={showMain}
                  >
                    <NavMobileDropdown
                      navData={{
                        ...navData,
                        dropdownLinks: [
                          {
                            title: "Account",
                            links:
                              navData.accountLinks?.map((link) => ({
                                ...link,
                                newWindow: false,
                              })) ?? [],
                          },
                        ],
                      }}
                      NavLinkTextStyles={NavLinkTextStyles}
                      selectedDropdownIndex={0}
                      closeDrawer={closeDrawer}
                    />
                  </NavMobileSecondaryScreen>
                )}

                {currentView.type === "category" && (
                  <NavMobileSecondaryScreen
                    title={currentView.category.title}
                    backCTAText={currentView.category.backCTAText}
                    closeAccordion={showMain}
                  >
                    <NavMobileCategory
                      navCategory={currentView.category}
                      closeDrawer={closeDrawer}
                    />
                  </NavMobileSecondaryScreen>
                )}
              </m.menu>
            </RemoveScroll>
          </Box>
        </ThemeWrapper>
      )}
    </AnimatePresence>
  );
};
